import React, { useEffect, useRef } from 'react';
import { BsTelephoneInboundFill, BsTelephoneXFill, BsCameraVideoFill } from 'react-icons/bs';
import { useRealtime } from '../../context/RealtimeContext';
import '../../styles/incomingCall.css';

// Simple looping ringtone via WebAudio so we don't need an audio asset.
function useRingtone(active) {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    let stopped = false;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;

    const beep = () => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 480;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.95);
    };

    beep();
    timerRef.current = setInterval(beep, 2000);

    if (navigator.vibrate) {
      navigator.vibrate([400, 200, 400, 200, 400]);
    }

    return () => {
      stopped = true;
      if (timerRef.current) clearInterval(timerRef.current);
      if (navigator.vibrate) navigator.vibrate(0);
      try { ctx.close(); } catch (_) { /* ignore */ }
    };
  }, [active]);
}

const IncomingCallModal = () => {
  const { incomingCall, acceptIncoming, declineIncoming } = useRealtime();
  useRingtone(Boolean(incomingCall));

  if (!incomingCall) return null;

  // If we're already on this booking's call page, the room handles the recall
  // prompt in-place — don't stack the global modal on top of it.
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(`/video-consultation/${incomingCall.bookingId}`)
  ) {
    return null;
  }

  return (
    <div className="incoming-call-overlay">
      <div className="incoming-call-card">
        <div className="incoming-call-pulse">
          {incomingCall.callerPhoto ? (
            <img src={incomingCall.callerPhoto} alt={incomingCall.callerName} />
          ) : (
            <div className="incoming-call-avatar-fallback">
              <BsCameraVideoFill />
            </div>
          )}
        </div>
        <div className="incoming-call-label">Incoming Video Consultation</div>
        <div className="incoming-call-name notranslate" translate="no">
          {incomingCall.callerName || 'Advocate'}
        </div>
        <div className="incoming-call-sub">is calling…</div>

        <div className="incoming-call-actions">
          <button type="button" className="incoming-call-btn decline" onClick={declineIncoming}>
            <BsTelephoneXFill />
            <span>Decline</span>
          </button>
          <button type="button" className="incoming-call-btn answer" onClick={acceptIncoming}>
            <BsTelephoneInboundFill />
            <span>Answer</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;
